// import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useState, useMemo, useContext, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import { VolumeResponse, Volume } from '~/shared/types/api';
import { SelectOption, Account } from '~/shared/utils/types';
import { DatafySwitchContext } from '~/shared/context/DatafySwitchProvider';
import { generateSkeletonRows, createSkeletonVolume } from '~/shared/utils/skeletonUtils';
import Table from '~/shared/components/Table';
import { useAuthContext } from '~/shared/context/useAuthContext';
import { isOrganizationView } from '~/shared/utils/accountUtils';

import TagsTooltip from '../../../common/TagsTooltip';
import { canBeDatafied } from '../../helpers';
import { getVolumeStableRowId } from '~/shared/utils/volumeHelpers';

import { AllVolumesTableHeader } from './AllVolumesTableHeader';
import { useAppliedFilters } from './hooks/useAppliedFilters';
import { useResponsiveHeaderHeight } from './hooks/useResponsiveHeaderHeight';
import { getColumnDefs } from './columnDefs';
import { usePermission } from '~/shared/hooks/usePermission';
import { useFilterUrlParams } from '../AllVolumesFiltersSidePanel/hooks/useFilterUrlParams';
import {
  getVolumeViewPreset,
  resolveVolumeView,
  VolumeViewPresetId,
} from '~/shared/utils/volumeViewPresets';
import { useVolumeViewPresetCounts } from '~/shared/hooks/useVolumeViewPresetCounts';

export const DATA_LIMIT = 50;

type VolumeWithSkeleton = Volume & { _isSkeleton?: boolean };

const sortKey = 'sort_field';
// const headerHeight = '88px';
// const navWidth = '4rem';

function AllVolumesTable({
  setPageOffset,
  pageOffset,
  sortField,
  data,
  loading = false,
  isTableFetching = false,
  accounts: filterAccounts,
}: {
  setPageOffset: React.Dispatch<React.SetStateAction<string>>;
  pageOffset: string;
  sortField: { field: string; dir: 'desc' | 'asc' };
  data:
  | ({ volumes: VolumeResponse['volumes']; count: number } & Partial<VolumeResponse>)
  | undefined;
  loading?: boolean;
  isTableFetching?: boolean;
  accounts: Account[];
}) {
  const [_searchParams, setSearchParams] = useSearchParams();
  const { datafySwitchVolumes } = useContext(DatafySwitchContext);
  const { accounts, activeTenantId } = useAuthContext();
  const canWrite = usePermission('datafy.write.autoscaling');

  const [selectedVolumes, setSelectedVolumes] = useState<string[]>([]);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [customCount, setCustomCount] = useState<number>();

  const {
    appliedClusterNameFilters,
    appliedInstanceFilters,
    appliedVolumeIdFilters,
    appliedVolumeTagFilters,
    appliedAccountFilters,
    appliedAutoscaleState,
    appliedEligibility,
    activeFilterCount,
    currentBaseFilters,
    needsAutoscalingUrlMigration,
  } = useAppliedFilters();

  const { applyAutoscalingFiltersOnly, resetFilters } = useFilterUrlParams();
  const { presetCounts, isLoading: presetCountsLoading } = useVolumeViewPresetCounts(currentBaseFilters);

  const { headerHeight, windowWidth } = useResponsiveHeaderHeight();

  const isOrgView = isOrganizationView(activeTenantId, accounts);

  useEffect(() => {
    if (!needsAutoscalingUrlMigration) {
      return;
    }

    applyAutoscalingFiltersOnly(appliedAutoscaleState, appliedEligibility);
  }, [
    needsAutoscalingUrlMigration,
    appliedAutoscaleState,
    appliedEligibility,
    applyAutoscalingFiltersOnly,
  ]);

  const resolvedVolumeView = resolveVolumeView(appliedAutoscaleState, appliedEligibility);
  const selectedPresetCount =
    resolvedVolumeView !== 'custom' ? presetCounts[resolvedVolumeView] : undefined;
  const selectedCategoryCount =
    resolvedVolumeView === 'custom' ? customCount ?? data?.count : selectedPresetCount;
  const uiCount = selectedCategoryCount;
  const paginationDataCount = uiCount ?? 0;
  const paginationTotalPages = Math.max(1, Math.ceil(paginationDataCount / DATA_LIMIT));

  useEffect(() => {
    if (isTableFetching || data?.count === undefined || resolvedVolumeView !== 'custom') {
      return;
    }
    setCustomCount((prev) => {
      if (prev === data.count) {
        return prev;
      }
      return data.count;
    });
  }, [data?.count, isTableFetching, resolvedVolumeView]);

  useEffect(() => {
    if (uiCount === undefined) {
      return;
    }
    const totalPages = Math.ceil(uiCount / DATA_LIMIT) || 1;
    const currentPageIdx = Math.floor((parseInt(pageOffset, 10) || 0) / DATA_LIMIT);
    if (currentPageIdx > totalPages - 1) {
      setPageOffset(`${(totalPages - 1) * DATA_LIMIT}`);
    }
  }, [uiCount, pageOffset, setPageOffset]);

  const toggleSidePanel = useCallback(() => setSidePanelOpen((o) => !o), []);
  const setPanelOpen = useCallback((open: boolean) => setSidePanelOpen(open), []);

  const rowData = useMemo(() => {
    if (loading && (!data?.volumes || data.volumes.length === 0)) {
      return generateSkeletonRows(DATA_LIMIT, createSkeletonVolume()) as VolumeWithSkeleton[];
    }
    return data?.volumes || [];
  }, [data?.volumes, loading]);

  const columnDefs = useMemo(
    () =>
      getColumnDefs({
        onSelectAllRows: () =>
          setSelectedVolumes((prev) =>
            prev?.length > 0
              ? []
              : data?.volumes.filter(canBeDatafied).map((v) => v.volumeId!) || []
          ),
        sortField,
        selectedVolumes,
        setSelectedVolumes,
        datafySwitchVolumes,
        windowWidth,
        canWrite,
        isOrgView,
        accounts,
      }),
    [
      sortField,
      datafySwitchVolumes,
      windowWidth,
      selectedVolumes,
      data?.volumes,
      isOrgView,
      accounts,
      canWrite,
    ]
  );

  const onPageChange = (page: number): void => {
    setSelectedVolumes([]);
    setPageOffset(`${page * DATA_LIMIT}`);
  };

  const handleResetFilters = useCallback(() => {
    setPageOffset('0');
    resetFilters();
    setSidePanelOpen(false);
  }, [resetFilters, setPageOffset]);

  const handleSelectVolumeViewPreset = useCallback(
    (presetId: VolumeViewPresetId) => {
      const preset = getVolumeViewPreset(presetId);
      setPageOffset('0');
      applyAutoscalingFiltersOnly(preset.stateIds, preset.eligibilityIds);
    },
    [applyAutoscalingFiltersOnly, setPageOffset]
  );

  // const isSkeletonLoading = loading && (!data?.volumes || data.volumes.length === 0);

  return (
    <div className={'flex flex-col min-h-[348px] h-full gap-2.5 2xl:gap-3.5'}>
      <AllVolumesTableHeader
        dataCount={uiCount}
        selectedCategoryCount={uiCount}
        activeFilterCount={activeFilterCount}
        sidePanelOpen={sidePanelOpen}
        onTogglePanel={toggleSidePanel}
        onSetPanelOpen={setPanelOpen}
        onResetFilters={handleResetFilters}
        onSelectVolumeViewPreset={handleSelectVolumeViewPreset}
        appliedClusterNameFilters={appliedClusterNameFilters as SelectOption[]}
        appliedInstanceFilters={appliedInstanceFilters as SelectOption[]}
        appliedVolumeIdFilters={appliedVolumeIdFilters as SelectOption[]}
        appliedVolumeTagFilters={appliedVolumeTagFilters as SelectOption[]}
        appliedAccountFilters={appliedAccountFilters as SelectOption[]}
        appliedAutoscaleState={appliedAutoscaleState}
        appliedEligibility={appliedEligibility}
        isOrgView={isOrgView}
        accounts={filterAccounts}
        activeOrgId={activeTenantId}
        activeFilters={currentBaseFilters}
        presetCounts={presetCounts}
        presetCountsLoading={presetCountsLoading}
        sortField={sortField}
      />
      <Table
        rowData={rowData}
        columnDefs={columnDefs}
        headerHeight={headerHeight.height}
        loading={false}
        disableClientSort
        suppressNoRowsWhileLoading={true}
        itemsName={'volumes'}
        getRowId={(params) => {
          const data = params.data as VolumeWithSkeleton;
          if (data && '_isSkeleton' in data && data._isSkeleton) {
            return getVolumeStableRowId(data) || 'skeleton';
          }
          return getVolumeStableRowId(data);
        }}
        pagination={{
          perPage: DATA_LIMIT,
          onChange: onPageChange,
          totalPages: paginationTotalPages,
          dataCount: paginationDataCount,
          itemsName: 'volumes',
          currentPage: Math.floor((parseInt(pageOffset, 10) || 0) / DATA_LIMIT),
        }}
        onSortChange={(sort) => {
          // Prevent sorting when loading skeleton data
          if (loading && (!data?.volumes || data.volumes.length === 0)) {
            return;
          }
          setSearchParams(
            (params) => {
              params.set(sortKey, JSON.stringify(sort));
              return params;
            },
            { replace: true }
          );
        }}
      />

      {/* Only render TagsTooltip for non-skeleton data */}
      {!loading &&
        rowData
          ?.filter((data) => {
            const skelData = data as VolumeWithSkeleton;
            return !('_isSkeleton' in skelData && skelData._isSkeleton);
          })
          ?.map((data) => (
            <TagsTooltip key={getVolumeStableRowId(data as Volume)} {...data} />
          ))}
    </div>
  );
}

export default AllVolumesTable;